import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
    Configuration, 
    ConfigurationMetadata,
    HierarchyConfigValue 
} from '@/app/types/api/configurationTypes';

interface ConfigurationState {
    // Configuration data
    configurations: Configuration[];
    currentConfiguration: Configuration | null;
    metadata: ConfigurationMetadata[];
    hierarchy: HierarchyConfigValue[];
    
    // UI state
    selectedCategory: string | null;
    selectedScope: string;
    searchQuery: string;
    
    // Loading states
    isLoadingList: boolean;
    isLoadingMetadata: boolean;
    isLoadingHierarchy: boolean;
    isSaving: boolean;
    isDeleting: boolean;
    
    // Error states
    error: string | null;
    
    // Pagination
    currentPage: number;
    pageSize: number;
    totalCount: number;
}

const initialState: ConfigurationState = {
    // Configuration data
    configurations: [],
    currentConfiguration: null,
    metadata: [],
    hierarchy: [],
    
    // UI state
    selectedCategory: null,
    selectedScope: '/global',
    searchQuery: '',
    
    // Loading states
    isLoadingList: false,
    isLoadingMetadata: false,
    isLoadingHierarchy: false,
    isSaving: false,
    isDeleting: false,
    
    // Error states
    error: null,
    
    // Pagination
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
};

const configurationSlice = createSlice({
    name: 'configuration',
    initialState,
    reducers: {
        // List operations
        startLoadingList: (state) => {
            state.isLoadingList = true;
            state.error = null;
        },
        
        completeLoadingList: (state, action: PayloadAction<{
            configurations: Configuration[];
            totalCount: number;
        }>) => {
            state.isLoadingList = false;
            state.configurations = action.payload.configurations;
            state.totalCount = action.payload.totalCount;
            state.error = null;
        },
        
        failLoadingList: (state, action: PayloadAction<string>) => {
            state.isLoadingList = false;
            state.error = action.payload;
        },
        
        // Metadata operations
        startLoadingMetadata: (state) => {
            state.isLoadingMetadata = true;
            state.error = null;
        },
        
        completeLoadingMetadata: (state, action: PayloadAction<ConfigurationMetadata[]>) => {
            state.isLoadingMetadata = false;
            state.metadata = action.payload;
            state.error = null;
        },
        
        failLoadingMetadata: (state, action: PayloadAction<string>) => {
            state.isLoadingMetadata = false;
            state.error = action.payload;
        },
        
        // Hierarchy operations
        startLoadingHierarchy: (state) => {
            state.isLoadingHierarchy = true;
            state.error = null;
        },
        
        completeLoadingHierarchy: (state, action: PayloadAction<HierarchyConfigValue[]>) => {
            state.isLoadingHierarchy = false;
            state.hierarchy = action.payload;
            state.error = null;
        },
        
        failLoadingHierarchy: (state, action: PayloadAction<string>) => {
            state.isLoadingHierarchy = false;
            state.error = action.payload;
        },
        
        // Save operations
        startSaving: (state) => {
            state.isSaving = true;
            state.error = null;
        },
        
        completeSaving: (state) => {
            state.isSaving = false;
            state.error = null;
        },
        
        failSaving: (state, action: PayloadAction<string>) => {
            state.isSaving = false;
            state.error = action.payload;
        },
        
        // Delete operations
        startDeleting: (state) => {
            state.isDeleting = true;
            state.error = null;
        },
        
        completeDeleting: (state, action: PayloadAction<string>) => {
            state.isDeleting = false;
            state.configurations = state.configurations.filter(
                (config: Configuration) => config.configKey !== action.payload
            );
            state.error = null;
        },
        
        failDeleting: (state, action: PayloadAction<string>) => {
            state.isDeleting = false;
            state.error = action.payload;
        },
        
        // UI state management
        setSelectedCategory: (state, action: PayloadAction<string | null>) => {
            state.selectedCategory = action.payload;
            state.currentPage = 1; // Reset to first page when category changes
        },
        
        setSelectedScope: (state, action: PayloadAction<string>) => {
            state.selectedScope = action.payload;
            state.currentPage = 1; // Reset to first page when scope changes
        },
        
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
            state.currentPage = 1; // Reset to first page when search changes
        },
        
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.currentPage = action.payload;
        },
        
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pageSize = action.payload;
            state.currentPage = 1; // Reset to first page when page size changes
        },
        
        // Current configuration
        setCurrentConfiguration: (state, action: PayloadAction<Configuration | null>) => {
            state.currentConfiguration = action.payload;
        },
        
        // Error management
        clearError: (state) => {
            state.error = null;
        },
        
        // Reset state
        resetConfigurationState: (_state) => {
            return initialState;
        },
    },
});

export const {
    // List operations
    startLoadingList,
    completeLoadingList,
    failLoadingList,
    
    // Metadata operations
    startLoadingMetadata,
    completeLoadingMetadata,
    failLoadingMetadata,
    
    // Hierarchy operations
    startLoadingHierarchy,
    completeLoadingHierarchy,
    failLoadingHierarchy,
    
    // Save operations
    startSaving,
    completeSaving,
    failSaving,
    
    // Delete operations
    startDeleting,
    completeDeleting,
    failDeleting,
    
    // UI state management
    setSelectedCategory,
    setSelectedScope,
    setSearchQuery,
    setCurrentPage,
    setPageSize,
    
    // Current configuration
    setCurrentConfiguration,
    
    // Error management
    clearError,
    
    // Reset state
    resetConfigurationState,
} = configurationSlice.actions;

export default configurationSlice.reducer;

// Selectors
export const selectConfiguration = (state: { configuration: ConfigurationState }) => state.configuration;
export const selectConfigurations = (state: { configuration: ConfigurationState }) => state.configuration.configurations;
export const selectCurrentConfiguration = (state: { configuration: ConfigurationState }) => state.configuration.currentConfiguration;
export const selectMetadata = (state: { configuration: ConfigurationState }) => state.configuration.metadata;
export const selectHierarchy = (state: { configuration: ConfigurationState }) => state.configuration.hierarchy;
export const selectSelectedCategory = (state: { configuration: ConfigurationState }) => state.configuration.selectedCategory;
export const selectSelectedScope = (state: { configuration: ConfigurationState }) => state.configuration.selectedScope;
export const selectSearchQuery = (state: { configuration: ConfigurationState }) => state.configuration.searchQuery;
export const selectIsLoadingList = (state: { configuration: ConfigurationState }) => state.configuration.isLoadingList;
export const selectIsLoadingMetadata = (state: { configuration: ConfigurationState }) => state.configuration.isLoadingMetadata;
export const selectIsLoadingHierarchy = (state: { configuration: ConfigurationState }) => state.configuration.isLoadingHierarchy;
export const selectIsSaving = (state: { configuration: ConfigurationState }) => state.configuration.isSaving;
export const selectIsDeleting = (state: { configuration: ConfigurationState }) => state.configuration.isDeleting;
export const selectConfigurationError = (state: { configuration: ConfigurationState }) => state.configuration.error;
export const selectCurrentPage = (state: { configuration: ConfigurationState }) => state.configuration.currentPage;
export const selectPageSize = (state: { configuration: ConfigurationState }) => state.configuration.pageSize;
export const selectTotalCount = (state: { configuration: ConfigurationState }) => state.configuration.totalCount;

// Computed selectors
export const selectIsAnyOperationInProgress = (state: { configuration: ConfigurationState }) =>
    state.configuration.isLoadingList || 
    state.configuration.isLoadingMetadata || 
    state.configuration.isLoadingHierarchy ||
    state.configuration.isSaving || 
    state.configuration.isDeleting;

export const selectMetadataByKey = (key: string) => (state: { configuration: ConfigurationState }) =>
    state.configuration.metadata.find(m => m.configKey === key) || null;

export const selectMetadataByCategory = (category: string) => (state: { configuration: ConfigurationState }) =>
    state.configuration.metadata.filter(m => m.category === category);

export const selectConfigurationsByCategory = (category: string) => (state: { configuration: ConfigurationState }) =>
    state.configuration.configurations.filter(c => c.category === category);

export const selectTotalPages = (state: { configuration: ConfigurationState }) =>
    Math.ceil(state.configuration.totalCount / state.configuration.pageSize);

export type { ConfigurationState };
